import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { AsyncPipe, KeyValuePipe, TitleCasePipe } from '@angular/common';

import {
  BehaviorSubject,
  catchError,
  concatMap,
  finalize,
  from,
  Observable,
  of,
  Subject,
  takeUntil,
} from 'rxjs';
import { MatIcon } from '@angular/material/icon';
import { MatDialog } from '@angular/material/dialog';

import { map, switchMap } from 'rxjs/operators';

import { TranslateModule } from '@ngx-translate/core';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';

import { IAttraction } from 'src/app/core/types/interfaces/attraction.interface';
import { OccasionGeniusResponse } from 'src/app/core/types/models/occasion-genius-response.model';
import { WeatherRendererComponent } from 'src/app/shared/components/weather-renderer/weather-renderer.component';

import { ReplaceStrPipe } from 'src/app/shared/pipes/replace-str.pipe';

import {
  IViatorCategories,
  ViatorComponent,
} from 'src/app/shared/used-components/viator/viator.component';
import { HotelsBookingContainerComponent } from 'src/app/shared/used-components/hotels-booking-container/hotels-booking-container.component';
import { OccasionGeniusWithFiltersComponent } from 'src/app/shared/components/occasion-genius-with-filters/occasion-genius-with-filters.component';

import { AuthService } from 'src/app/core/services/auth.service';
import { PackageService } from 'src/app/core/services/package.service';
import { CountryInfoService } from 'src/app/core/services/country-info.service';
import { OccasionGeniusService } from 'src/app/core/services/occasion-genius.service';
import {
  WeatherDisplayModel,
  WeatherForecastService,
} from 'src/app/core/services/weather-forecast.service';
import { BreakpointService } from 'src/app/core/services/breakpoint.service';
import { ScrollToIdService } from 'src/app/core/services/scroll-to-id.service';
import { CountryInfoTextService } from '../../../../country-info/services/country-info-text.service';
import { CountryInfoParseService } from '../../../../country-info/services/country-info-parse.service';

import { AudioTour } from 'src/app/core/types/models/audio-tour';
import { SafeHtmlPipe } from 'src/app/shared/pipes/safe-html.pipe';
import { SwiperDirective } from 'src/app/shared/directives/swiper.directive';

import { Trip } from 'src/app/shared/classes/trip';
import { ESvgIcons } from 'src/app/core/enums/svg-icons';
import { EScrollPrefix } from 'src/app/core/enums/scroll-to.enum';
import {
  IStopover,
  IParsedTripLeg,
  ITripDestination,
} from 'src/app/core/types/interfaces/trip-elements.interface';
import {
  AuthDialogComponent,
  EAuthPage,
} from 'src/app/shared/dialogs/auth-dialog/auth-dialog.component';
import { GalleryComponent } from 'src/app/shared/components/gallery/gallery.component';
import { StopoverListComponent } from '../stopover/stopover-list/stopover-list.component';
import { AudioToursComponent } from 'src/app/shared/used-components/audio-tours/audio-tours.component';
import { TextEllipsisComponent } from 'src/app/shared/components/text-ellipsis/text-ellipsis.component';
import { NoItemsFoundComponent } from 'src/app/shared/components/no-items-found/no-items-found.component';
import { BookingBannerComponent } from 'src/app/shared/components/booking-banner/booking-banner.component';
import { OccasionGeniusComponent } from 'src/app/shared/components/occasion-genius/occasion-genius.component';
import { GalleryMobileComponent } from '../../../../countries/components/mobile/gallery-mobile/gallery-mobile.component';

import { SwiperOptions } from 'swiper/types/swiper-options';

import moment from 'moment';

@Component({
  selector: 'app-trip-city-info',
  templateUrl: './trip-city-info.component.html',
  styleUrl: './trip-city-info.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    CountryInfoService,
    CountryInfoParseService,
    OccasionGeniusService,
    CountryInfoTextService,
  ],
  imports: [
    TitleCasePipe,
    NgxSkeletonLoaderModule,
    SafeHtmlPipe,
    ReplaceStrPipe,
    TranslateModule,
    KeyValuePipe,
    SwiperDirective,
    MatIcon,
    AsyncPipe,
    ViatorComponent,
    GalleryComponent,
    AudioToursComponent,
    StopoverListComponent,
    TextEllipsisComponent,
    NoItemsFoundComponent,
    GalleryMobileComponent,
    BookingBannerComponent,
    OccasionGeniusComponent,
    WeatherRendererComponent,
    HotelsBookingContainerComponent,
    OccasionGeniusWithFiltersComponent,
  ],
})
export class TripCityInfoComponent implements OnInit, OnChanges, OnDestroy {
  @Input() legs: IParsedTripLeg[] = [];
  @Input() trip: Trip;
  @Input() startDate: string = '';
  @Input() endDate: string = '';
  @Input() packages: any[] = [];
  @Input() destinations: ITripDestination[] = [];
  @Input() cityInfoIndex: number = -1;
  @Output() onCityIndexChange = new EventEmitter<number>();

  public numOfHurt: number = 0;
  public occasionGeniusResponse: OccasionGeniusResponse =
    new OccasionGeniusResponse();
  public isFetching: boolean = false;
  public isWetterFetching: boolean = false;
  public wheather: WeatherDisplayModel;

  public anchorSwiperOptions: SwiperOptions = {
    slidesPerView: 'auto',
    spaceBetween: 10,
    slidesOffsetAfter: 12,
    slidesOffsetBefore: 12,
    freeMode: true,
  };

  protected readonly eSvgIcons = ESvgIcons;
  protected readonly destroySubject$: Subject<void> = new Subject<void>();

  private readonly id$ = new BehaviorSubject<number | null>(null);

  constructor(
    protected countryInfoTextService: CountryInfoTextService,
    protected breakpointService: BreakpointService,
    private packageService: PackageService,
    private occasionGeniusService: OccasionGeniusService,
    private authService: AuthService,
    private weatherForecastService: WeatherForecastService,
    private matDialog: MatDialog,
    private cdr: ChangeDetectorRef,
    private scrollToIdService: ScrollToIdService,
  ) {}

  get packageCity(): any {
    return this.packages[this.cityInfoIndex];
  }

  get itineraryItem(): ITripDestination | null {
    return this.destinations[this.cityInfoIndex] || null;
  }

  get cityLeg(): IParsedTripLeg {
    return this.legs[this.cityInfoIndex];
  }

  get id(): number | null {
    return this.itineraryItem?.id || null;
  }

  get name(): string {
    return this.itineraryItem?.cityName || '';
  }

  get countryName(): string {
    return this.itineraryItem?.countryName || '';
  }

  get audioTours(): AudioTour[] {
    return this.itineraryItem?.guidedTours || [];
  }

  get attractions(): IAttraction[] {
    return this.itineraryItem?.attractions || [];
  }

  get viatorEvents(): IViatorCategories {
    if (!this.packageCity) return {};
    return this.packageService.packageCityEventMap[
      this.packageService.getViatorEventId(this.packageCity)
    ];
  }

  get description(): string {
    return (
      this.itineraryItem?.extraDescription ||
      this.itineraryItem?.shortDescription ||
      ''
    );
  }

  get stopovers(): IStopover[] {
    return this.itineraryItem?.stopovers || [];
  }

  get startCityName(): string {
    return this.destinations[0]?.cityName || '';
  }

  get endCityName(): string {
    return this.destinations[this.destinations.length - 1]?.cityName || '';
  }

  get tripDates(): { startDate: string; endDate: string } {
    const { isSpecifyDates, tripStartDate, numDays } = this.trip;

    const startDate: string = moment(tripStartDate)
      .add(isSpecifyDates ? 0 : 1, 'day')
      .toISOString();
    const endDate: string = moment(tripStartDate)
      .add(numDays, 'days')
      .toISOString();
    return { startDate, endDate };
  }

  get userStatus(): Observable<boolean> {
    return this.authService.isLogged;
  }

  ngOnInit() {
    this.fetchCityInfo();
    this.authService.isLogged$
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(() => this.cdr.markForCheck());
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      changes &&
      changes.packages &&
      changes.packages?.currentValue?.length !==
        changes.packages?.previousValue?.length
    ) {
      if (this.packages.length) {
        this.id$.next(this.id);
      }
    }
    if (
      changes &&
      changes.cityInfoIndex &&
      changes.cityInfoIndex?.currentValue !==
        changes.cityInfoIndex?.previousValue
    ) {
      if (this.packages.length) {
        this.id$.next(this.id);
      }
    }
  }

  ngOnDestroy() {
    this.destroySubject$.next();
    this.destroySubject$.complete();
  }

  getElementId(index: number): string {
    return `element-id-${index}`;
  }

  setAuth(isLogin = false): void {
    this.matDialog
      .open(AuthDialogComponent, {
        width: '1150px',
        maxWidth: '98vw',
        maxHeight: '98dvh',
        panelClass: 'mobile-container',
        data: {
          pageType: isLogin ? EAuthPage.LOGIN : EAuthPage.SIGN_UP,
        },
      })
      .afterClosed()
      .subscribe(() => {
        this.cdr.markForCheck();
      });
  }

  scrollToAnchor(elementId: number, e?: MouseEvent): void {
    if (e) e.preventDefault();
    this.scrollToIdService.scrollToWithContainer(
      elementId,
      EScrollPrefix.ELEMENT,
      '.mobile-planner__services-info',
      130,
    );
  }

  private fetchCityInfo(): void {
    this.isFetching = true;
    this.id$
      .pipe(
        switchMap((id) => {
          if (typeof id !== 'number' || !this.packageCity || !this.trip) {
            return [];
          }

          this.isWetterFetching = true;
          return this.weatherForecastService.getWeatherByCity(this.name).pipe(
            concatMap((weatherResponse) => {
              if (weatherResponse) {
                this.wheather = weatherResponse;
                this.cdr.markForCheck();
              }

              return from(
                this.packageService.getViatorCityEvents(this.packageCity),
              ).pipe(
                map((viatorResponse) => ({ weatherResponse, viatorResponse })),
                catchError((error) => {
                  console.error('Error in ViatorCityEvents:', error);
                  return of({ weatherResponse, viatorResponse: null });
                }),
              );
            }),
            concatMap((responses) => {
              this.cdr.markForCheck();
              return this.occasionGeniusService
                .getOccasionGeniusAll(this.id, {
                  from: this.packageService.packages$.value[this.cityInfoIndex]
                    .dates.start,
                  to: this.packageService.packages$.value[this.cityInfoIndex]
                    .dates.end,
                  offset: 0,
                })
                .pipe(
                  map((occasionGeniusResponse) => ({
                    ...responses,
                    occasionGeniusResponse,
                  })),
                  catchError((error) => {
                    console.error('Error in OccasionGeniusService:', error);
                    return of({ ...responses, occasionGeniusResponse: null });
                  }),
                );
            }),
            finalize(() => {
              this.isFetching = false;
              this.cdr.markForCheck();
            }),
            catchError((error) => {
              console.error('Error in WeatherForecastService:', error);
              return of(null);
            }),
          );
        }),
        takeUntil(this.destroySubject$),
      )
      .subscribe({
        next: ({ occasionGeniusResponse }) => {
          if (occasionGeniusResponse) {
            this.occasionGeniusResponse = new OccasionGeniusResponse(
              occasionGeniusResponse,
            );
          }
        },
        error: () => {
          this.isFetching = false;
          this.cdr.markForCheck();
        },
      });
  }
}
